/**
 * Helpers for resolving the wrapper's auto-attached base properties.
 * Used by both the server and client wrappers; keep them framework-free
 * so the client bundle doesn't pull in `next/headers`.
 */

const ADMIN_EMAIL_SUFFIX = "@listenlabs.ai";

/**
 * Shared rule for `is_admin_viewer`: true when the user is on the Listen
 * Labs team. Used by both the server wrapper (sourced from auth claims)
 * and the client provider (sourced from `UserInfo`) so the two can't drift.
 */
export const resolveIsAdminViewer = ({
  email,
  isSuperAdmin,
}: {
  email: string | null | undefined;
  isSuperAdmin: boolean;
}): boolean => email?.endsWith(ADMIN_EMAIL_SUFFIX) === true || isSuperAdmin;

export const isAdminViewerFromClaims = (
  claims: Record<string, unknown> | null | undefined
): boolean => {
  if (!claims) return false;
  return resolveIsAdminViewer({
    email: typeof claims.email === "string" ? claims.email : null,
    isSuperAdmin: claims.is_super_admin === true,
  });
};

/**
 * The product area an event belongs to: the first dotted segment of the
 * event name (`analysis.report.viewed` -> `analysis`). Auto-attached as
 * the `surface` property so dashboards can break down / filter by area.
 */
export const getEventSurface = (event: string): string =>
  event.split(".")[0] ?? event;

/**
 * Property keys the wrapper attaches automatically. Callers passing
 * these inside `properties` is a bug (the value would either be
 * overwritten by the wrapper or, worse, contradict the derived value).
 * The `analytics-no-base-properties-in-track-properties` ESLint rule
 * keys off this set.
 */
export const BASE_PROPERTY_KEYS = [
  "user_id",
  "userId",
  "org_id",
  "orgId",
  "is_admin_viewer",
  "isAdminViewer",
  "client_session_id",
  "clientSessionId",
  "viewer_kind",
  "viewerKind",
  "deployment_region",
  "app_env",
  "app_version",
] as const;
